/**
 * Host-aware API URL rewriting.
 *
 * REACT_APP_BACKEND_URL is baked into the build at compile time (CRA substitutes
 * string literals during `yarn build`). That's perfect for the preview
 * environment where everything runs on a single deploy URL, but it breaks the
 * moment a custom domain (e.g. fastprototype.ai) points at the same cluster —
 * because every API call would fire off at the baked-in preview hostname, which
 * either triggers CORS / 404s, or results in confusing behaviour like
 * "factory-reset data came back" if the preview and the custom domain are
 * served by different backends.
 *
 * This module installs a global axios request interceptor that rewrites any
 * URL starting with the baked REACT_APP_BACKEND_URL to use the visitor's
 * current origin whenever the two differ — which is exactly what we want for
 * production on a custom domain, and a no-op for preview.
 *
 * It also exports a tiny helper `apiUrl(path)` for code that wants to compute
 * the final base URL explicitly.
 */
import axios from "axios";

const BUILD_BASE = process.env.REACT_APP_BACKEND_URL || "";

function sameOriginForApi() {
  if (typeof window === "undefined") return BUILD_BASE;
  try {
    const current = window.location.origin;
    if (!BUILD_BASE) return current;
    const built = new URL(BUILD_BASE);
    const here = new URL(current);
    // Same host → keep the baked URL (preview runtime). Different host → rewrite.
    return built.host === here.host ? BUILD_BASE : current;
  } catch {
    return BUILD_BASE;
  }
}

export function apiBase() {
  return sameOriginForApi();
}

export function apiUrl(path) {
  const base = apiBase();
  if (!path) return base;
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** Install the global axios rewriter. Call once from index.js.
 *
 * IMPORTANT: axios's per-instance interceptors are NOT inherited from the
 * default axios. Components that use `axios.create({...})` (we have several:
 * `AdminUsersModal`, `AdminProjectDetail`, `CustomerDashboard`, …) would
 * therefore bypass the rewriter and fire requests at the baked-in
 * REACT_APP_BACKEND_URL even when the visitor is on a different host (e.g.
 * `fastprototype.ai`), producing CORS / "Network error" failures in
 * production.
 *
 * To fix that universally we patch `axios.create` itself so every new
 * instance auto-installs the same rewriter when it is constructed.
 */
function _attachRewriter(instance) {
  if (!instance || typeof instance.interceptors?.request?.use !== "function") return;
  if (instance.__lfHostRewriterInstalled) return;
  instance.__lfHostRewriterInstalled = true;
  instance.interceptors.request.use((config) => {
    const base = sameOriginForApi();
    if (!BUILD_BASE || base === BUILD_BASE) return config;
    try {
      if (typeof config.url === "string" && config.url.startsWith(BUILD_BASE)) {
        config.url = base + config.url.slice(BUILD_BASE.length);
      }
      if (typeof config.baseURL === "string" && config.baseURL.startsWith(BUILD_BASE)) {
        config.baseURL = base + config.baseURL.slice(BUILD_BASE.length);
      }
    } catch {
      /* noop */
    }
    return config;
  });
}

export function installApiHostRewriter() {
  if (typeof window === "undefined") return;
  // Default axios (used by anything that imports `axios` and calls .get/.post directly).
  _attachRewriter(axios);
  // Patch the factory so any future `axios.create({...})` instance inherits the rewriter.
  const origCreate = axios.create.bind(axios);
  if (!axios.__lfCreatePatched) {
    axios.__lfCreatePatched = true;
    axios.create = (config) => {
      const instance = origCreate(config);
      _attachRewriter(instance);
      return instance;
    };
  }
}
